/*
📊 Safety Intelligence Reports Component
Türkiye'nin En Gelişmiş Safety Intelligence Sistemi - Raporlar Modülü
*/

import React, { useState, useEffect } from 'react';
import {
  Card,
  Row,
  Col,
  Statistic,
  Table,
  Select,
  DatePicker,
  Button,
  Typography,
  Space,
  Tag,
  Progress,
  Tabs,
  Alert,
  Tooltip,
  Modal,
  Spin,
  Empty,
  List,
  Avatar
} from 'antd';
import {
  BarChartOutlined,
  LineChartOutlined,
  PieChartOutlined,
  DownloadOutlined,
  PrinterOutlined,
  CalendarOutlined,
  FilterOutlined,
  ExclamationCircleOutlined,
  CheckCircleOutlined,
  ClockCircleOutlined,
  WarningOutlined,
  SafetyCertificateOutlined,
  TrophyOutlined,
  RiseOutlined,
  FallOutlined,
  EyeOutlined,
  FileTextOutlined
} from '@ant-design/icons';
import { useTheme } from '../../hooks/useTheme';
import { ThreatDetailModal } from './ThreatDetailModal';
import type { ColumnsType } from 'antd/es/table';
import dayjs from 'dayjs';

const { Title, Text, Paragraph } = Typography;
const { Option } = Select;
const { RangePicker } = DatePicker;

interface ThreatReport {
  id: string;
  timestamp: string;
  type: string;
  severity: 'low' | 'medium' | 'high' | 'critical';
  location: string;
  description: string;
  status: 'detected' | 'investigating' | 'resolved' | 'false_positive';
  confidence: number;
  responseTime: number; // minutes
}

interface PerformanceMetrics {
  totalThreats: number;
  resolvedThreats: number;
  avgResponseTime: number;
  falsePositiveRate: number;
  systemUptime: number;
  dataProcessed: number;
}

interface GeographicData {
  region: string;
  threatCount: number;
  riskLevel: string;
  lastIncident: string;
}

export const SafetyIntelligenceReports: React.FC = () => {
  const { isDarkMode } = useTheme();
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('overview');
  const [dateRange, setDateRange] = useState([dayjs().subtract(30, 'day'), dayjs()]);
  const [reportPeriod, setReportPeriod] = useState('30d');
  const [selectedRegion, setSelectedRegion] = useState('all');
  
  // Threat detail modal state
  const [threatDetailVisible, setThreatDetailVisible] = useState(false);
  const [selectedThreat, setSelectedThreat] = useState<ThreatReport | null>(null);

  // Mock data
  const [performanceMetrics, setPerformanceMetrics] = useState<PerformanceMetrics>({
    totalThreats: 1247,
    resolvedThreats: 1195,
    avgResponseTime: 3.2,
    falsePositiveRate: 4.2,
    systemUptime: 99.8,
    dataProcessed: 2.4 // TB
  });

  const [threatReports, setThreatReports] = useState<ThreatReport[]>([
    {
      id: 'TR-2024-001',
      timestamp: '2024-08-16 14:30:00',
      type: 'Şüpheli Aktivite',
      severity: 'high',
      location: 'Kadıköy, İstanbul',
      description: 'Anormal davranış kalıbı tespit edildi',
      status: 'resolved',
      confidence: 87,
      responseTime: 2.5
    },
    {
      id: 'TR-2024-002',
      timestamp: '2024-08-16 16:45:00',
      type: 'Acil Durum İhbarı',
      severity: 'critical',
      location: 'Beyoğlu, İstanbul',
      description: 'Kayıp kişi ihbarı alındı',
      status: 'investigating',
      confidence: 94,
      responseTime: 1.2
    },
    {
      id: 'TR-2024-003',
      timestamp: '2024-08-16 18:20:00',
      type: 'Güvenlik İhlali',
      severity: 'medium',
      location: 'Şişli, İstanbul',
      description: 'Yetkisiz erişim denemesi',
      status: 'resolved',
      confidence: 76,
      responseTime: 4.8
    }
  ]);

  const [geographicData, setGeographicData] = useState<GeographicData[]>([
    { region: 'İstanbul - Avrupa', threatCount: 342, riskLevel: 'Orta', lastIncident: '2024-08-16 18:20' },
    { region: 'İstanbul - Anadolu', threatCount: 287, riskLevel: 'Düşük', lastIncident: '2024-08-16 16:45' },
    { region: 'Ankara', threatCount: 156, riskLevel: 'Düşük', lastIncident: '2024-08-15 22:10' },
    { region: 'İzmir', threatCount: 198, riskLevel: 'Orta', lastIncident: '2024-08-16 12:30' },
    { region: 'Bursa', threatCount: 89, riskLevel: 'Düşük', lastIncident: '2024-08-15 14:20' }
  ]);

  // Rapor oluştur
  const generateReport = async () => {
    setLoading(true);
    try {
      // Simulate API call
      await new Promise(resolve => setTimeout(resolve, 2000));
      
      // Mock report generation
      const reportData = {
        period: `${dateRange[0]?.format('DD.MM.YYYY')} - ${dateRange[1]?.format('DD.MM.YYYY')}`,
        metrics: performanceMetrics,
        threats: threatReports,
        geographic: geographicData
      };
      
      // Download simulation
      const blob = new Blob([JSON.stringify(reportData, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `safety-intelligence-report-${dayjs().format('YYYY-MM-DD')}.json`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      
    } finally {
      setLoading(false);
    }
  };

  // Tehdit detayını göster
  const showThreatDetail = (threat: ThreatReport) => {
    setSelectedThreat(threat);
    setThreatDetailVisible(true);
  };

  // Modal kapat
  const closeThreatDetail = () => {
    setThreatDetailVisible(false);
    setSelectedThreat(null);
  };

  // Severity renk mapping
  const getSeverityColor = (severity: string) => {
    switch (severity) {
      case 'critical': return '#ff4d4f';
      case 'high': return '#fa8c16';
      case 'medium': return '#faad14';
      case 'low': return '#52c41a';
      default: return '#d9d9d9';
    }
  };

  // Status renk mapping
  const getStatusColor = (status: string) => {
    switch (status) {
      case 'resolved': return 'green';
      case 'investigating': return 'orange';
      case 'detected': return 'blue';
      case 'false_positive': return 'red';
      default: return 'default';
    }
  };

  // Risk level renk mapping
  const getRiskLevelColor = (level: string) => {
    switch (level) {
      case 'Yüksek': return 'red';
      case 'Orta': return 'orange';
      case 'Düşük': return 'green';
      default: return 'default';
    }
  };

  // Threat reports table columns
  const threatColumns: ColumnsType<ThreatReport> = [
    {
      title: 'Rapor ID',
      dataIndex: 'id',
      key: 'id',
      width: 120,
      render: (text) => <Text code>{text}</Text>
    },
    {
      title: 'Zaman',
      dataIndex: 'timestamp',
      key: 'timestamp',
      width: 160,
      render: (text) => dayjs(text).format('DD.MM.YYYY HH:mm')
    },
    {
      title: 'Tür',
      dataIndex: 'type',
      key: 'type',
      width: 150
    },
    {
      title: 'Önem',
      dataIndex: 'severity',
      key: 'severity',
      width: 100,
      render: (severity) => (
        <Tag color={getSeverityColor(severity)}>
          {severity.toUpperCase()}
        </Tag>
      )
    },
    {
      title: 'Konum',
      dataIndex: 'location',
      key: 'location',
      width: 150
    },
    {
      title: 'Durum',
      dataIndex: 'status',
      key: 'status',
      width: 120,
      render: (status) => {
        const statusMap = {
          detected: 'Tespit Edildi',
          investigating: 'İnceleniyor',
          resolved: 'Çözüldü',
          false_positive: 'Yanlış Alarm'
        };
        return <Tag color={getStatusColor(status)}>{statusMap[status as keyof typeof statusMap]}</Tag>;
      }
    },
    {
      title: 'Güven',
      dataIndex: 'confidence',
      key: 'confidence',
      width: 100,
      render: (confidence) => (
        <Progress 
          percent={confidence} 
          size="small" 
          status={confidence > 80 ? 'success' : confidence > 60 ? 'active' : 'exception'}
          showInfo={false}
        />
      )
    },
    {
      title: 'Müdahale',
      dataIndex: 'responseTime',
      key: 'responseTime',
      width: 100,
      render: (time) => `${time} dk`
    },
    {
      title: 'İşlem',
      key: 'action',
      width: 80,
      render: (_, record) => (
        <Tooltip title="Detayları Görüntüle">
          <Button 
            type="text" 
            icon={<EyeOutlined />} 
            size="small" 
            onClick={() => showThreatDetail(record)}
          />
        </Tooltip>
      )
    }
  ];

  // Geographic data table columns
  const geographicColumns: ColumnsType<GeographicData> = [
    {
      title: 'Bölge',
      dataIndex: 'region',
      key: 'region'
    },
    {
      title: 'Tehdit Sayısı',
      dataIndex: 'threatCount',
      key: 'threatCount',
      render: (count) => <Text strong>{count.toLocaleString()}</Text>
    },
    {
      title: 'Risk Seviyesi',
      dataIndex: 'riskLevel',
      key: 'riskLevel',
      render: (level) => <Tag color={getRiskLevelColor(level)}>{level}</Tag>
    },
    {
      title: 'Son Olay',
      dataIndex: 'lastIncident',
      key: 'lastIncident',
      render: (text) => dayjs(text).format('DD.MM.YYYY HH:mm')
    }
  ];

  // Genel Bakış Tab
  const OverviewTab = () => (
    <Row gutter={[24, 24]}>
      {/* Performance Metrics */}
      <Col span={24}>
        <Row gutter={16}>
          <Col span={6}>
            <Card>
              <Statistic
                title="Toplam Tehdit"
                value={performanceMetrics.totalThreats}
                prefix={<ExclamationCircleOutlined />}
                valueStyle={{ color: '#fa8c16' }}
                suffix={
                  <Tooltip title="Son 30 günde tespit edilen toplam tehdit sayısı">
                    <Text type="secondary" style={{ fontSize: 12 }}>30 gün</Text>
                  </Tooltip>
                }
              />
            </Card>
          </Col>
          <Col span={6}>
            <Card>
              <Statistic
                title="Çözülen Tehditler"
                value={performanceMetrics.resolvedThreats}
                prefix={<CheckCircleOutlined />}
                valueStyle={{ color: '#52c41a' }}
                suffix={
                  <Text style={{ fontSize: 12, color: '#52c41a' }}>
                    %{((performanceMetrics.resolvedThreats / performanceMetrics.totalThreats) * 100).toFixed(1)}
                  </Text>
                }
              />
            </Card>
          </Col>
          <Col span={6}>
            <Card>
              <Statistic
                title="Ortalama Müdahale"
                value={performanceMetrics.avgResponseTime}
                prefix={<ClockCircleOutlined />}
                precision={1}
                suffix="dk"
                valueStyle={{ color: '#1890ff' }}
              />
            </Card>
          </Col>
          <Col span={6}>
            <Card>
              <Statistic
                title="Sistem Çalışma Süresi"
                value={performanceMetrics.systemUptime}
                prefix={<TrophyOutlined />}
                precision={1}
                suffix="%"
                valueStyle={{ color: '#52c41a' }}
              />
            </Card>
          </Col>
        </Row>
      </Col>

      {/* System Performance Alert */}
      <Col span={24}>
        <Alert
          message="Sistem Performansı Mükemmel"
          description={`Son 30 günde ${performanceMetrics.totalThreats} tehdit tespit edildi, ${performanceMetrics.resolvedThreats} tanesi başarıyla çözüldü. Ortalama müdahale süresi ${performanceMetrics.avgResponseTime} dakika.`}
          type="success"
          icon={<TrophyOutlined />}
          showIcon
          style={{ marginBottom: 24 }}
        />
      </Col>

      {/* Recent Threats */}
      <Col span={24}>
        <Card 
          title={
            <Space>
              <WarningOutlined />
              <span>Son Tehdit Raporları</span>
            </Space>
          }
          extra={
            <Space>
              <Text type="secondary">Toplam: {threatReports.length}</Text>
              <Button type="link" size="small">Tümünü Gör</Button>
            </Space>
          }
        >
          <Table
            columns={threatColumns}
            dataSource={threatReports}
            size="small"
            pagination={{ pageSize: 5, showSizeChanger: false }}
            rowKey="id"
          />
        </Card>
      </Col>
    </Row>
  );

  // Coğrafi Analiz Tab
  const GeographicTab = () => (
    <Row gutter={[24, 24]}>
      <Col span={24}>
        <Card 
          title={
            <Space>
              <BarChartOutlined />
              <span>Bölgesel Tehdit Dağılımı</span>
            </Space>
          }
          extra={
            <Select 
              value={selectedRegion} 
              onChange={setSelectedRegion}
              style={{ width: 150 }}
            >
              <Option value="all">Tüm Bölgeler</Option>
              <Option value="istanbul">İstanbul</Option>
              <Option value="ankara">Ankara</Option>
              <Option value="izmir">İzmir</Option>
            </Select>
          }
        >
          <Table
            columns={geographicColumns}
            dataSource={geographicData}
            pagination={false}
            rowKey="region"
          />
        </Card>
      </Col>

      <Col span={12}>
        <Card title="Risk Haritası">
          <div style={{ textAlign: 'center', padding: '40px 0' }}>
            <Text type="secondary">🗺️ Interaktif harita yakında eklenecek</Text>
          </div>
        </Card>
      </Col>

      <Col span={12}>
        <Card title="Hotspot Analizi">
          <List
            size="small"
            dataSource={geographicData.slice(0, 3)}
            renderItem={(item, index) => (
              <List.Item>
                <List.Item.Meta
                  avatar={<Avatar style={{ backgroundColor: getSeverityColor(item.riskLevel === 'Yüksek' ? 'high' : item.riskLevel === 'Orta' ? 'medium' : 'low') }}>{index + 1}</Avatar>}
                  title={item.region}
                  description={`${item.threatCount} tehdit, Risk: ${item.riskLevel}`}
                />
              </List.Item>
            )}
          />
        </Card>
      </Col>
    </Row>
  );

  // Performans Tab
  const PerformanceTab = () => (
    <Row gutter={[24, 24]}>
      <Col span={24}>
        <Alert
          message="Sistem Performansı"
          description="Safety Intelligence sisteminin performans metrikleri ve trend analizleri"
          type="info"
          style={{ marginBottom: 24 }}
        />
      </Col>

      <Col span={8}>
        <Card title="Yanıt Süresi Trendi">
          <div style={{ textAlign: 'center', padding: 40 }}>
            <Text style={{ fontSize: 36, color: '#52c41a' }}>
              {performanceMetrics.avgResponseTime}dk
            </Text>
            <br />
            <Text type="secondary">Ortalama Müdahale</Text>
            <br />
            <Space style={{ marginTop: 8 }}>
              <RiseOutlined style={{ color: '#52c41a' }} />
              <Text style={{ color: '#52c41a', fontSize: 12 }}>%15 iyileşme</Text>
            </Space>
          </div>
        </Card>
      </Col>

      <Col span={8}>
        <Card title="Doğruluk Oranı">
          <div style={{ textAlign: 'center', padding: 40 }}>
            <Progress
              type="circle"
              percent={Math.round((1 - performanceMetrics.falsePositiveRate / 100) * 100)}
              format={(percent) => `${percent}%`}
              strokeColor="#52c41a"
            />
            <br />
            <Text type="secondary" style={{ marginTop: 8 }}>Yanlış Alarm: %{performanceMetrics.falsePositiveRate}</Text>
          </div>
        </Card>
      </Col>

      <Col span={8}>
        <Card title="Sistem Durumu">
          <div style={{ textAlign: 'center', padding: 40 }}>
            <Text style={{ fontSize: 36, color: '#52c41a' }}>
              {performanceMetrics.systemUptime}%
            </Text>
            <br />
            <Text type="secondary">Çalışma Süresi</Text>
            <br />
            <Text style={{ color: '#52c41a', fontSize: 12 }}>
              {Math.round(performanceMetrics.dataProcessed * 10) / 10} TB İşlendi
            </Text>
          </div>
        </Card>
      </Col>
    </Row>
  );

  const tabItems = [
    {
      key: 'overview',
      label: (
        <Space>
          <PieChartOutlined />
          <span>Genel Bakış</span>
        </Space>
      ),
      children: <OverviewTab />
    },
    {
      key: 'geographic',
      label: (
        <Space>
          <BarChartOutlined />
          <span>Coğrafi Analiz</span>
        </Space>
      ),
      children: <GeographicTab />
    },
    {
      key: 'performance',
      label: (
        <Space>
          <LineChartOutlined />
          <span>Performans</span>
        </Space>
      ),
      children: <PerformanceTab />
    }
  ];

  return (
    <div style={{ padding: '24px', background: isDarkMode ? '#13171c' : '#f8fafc', minHeight: '100vh' }}>
      <div style={{ marginBottom: 24 }}>
        <Title level={2} style={{ margin: 0, display: 'flex', alignItems: 'center', gap: 12 }}>
          <FileTextOutlined />
          Safety Intelligence Raporları
        </Title>
        <Paragraph style={{ margin: '8px 0 0 0', color: isDarkMode ? '#c9c9c9' : '#6b7280' }}>
          Detaylı analiz ve performans raporları - Türkiye'nin en gelişmiş güvenlik istihbarat sistemi
        </Paragraph>
      </div>

      {/* Report Controls */}
      <Card style={{ marginBottom: 24 }}>
        <Row gutter={16} align="middle">
          <Col flex="auto">
            <Space wrap>
              <div>
                <Text strong>Rapor Dönemi:</Text>
                <RangePicker
                  value={dateRange}
                  onChange={(dates) => setDateRange(dates || [dayjs().subtract(30, 'day'), dayjs()])}
                  style={{ marginLeft: 8 }}
                />
              </div>
              <div>
                <Text strong>Hızlı Seçim:</Text>
                <Select 
                  value={reportPeriod} 
                  onChange={setReportPeriod}
                  style={{ width: 120, marginLeft: 8 }}
                >
                  <Option value="7d">Son 7 Gün</Option>
                  <Option value="30d">Son 30 Gün</Option>
                  <Option value="90d">Son 3 Ay</Option>
                  <Option value="1y">Son 1 Yıl</Option>
                </Select>
              </div>
            </Space>
          </Col>
          <Col>
            <Space>
              <Button icon={<FilterOutlined />}>
                Filtrele
              </Button>
              <Button icon={<PrinterOutlined />}>
                Yazdır
              </Button>
              <Button 
                type="primary"
                icon={<DownloadOutlined />}
                loading={loading}
                onClick={generateReport}
              >
                Rapor İndir
              </Button>
            </Space>
          </Col>
        </Row>
      </Card>

      {/* Report Content */}
      <Card>
        <Tabs 
          activeKey={activeTab} 
          onChange={setActiveTab}
          items={tabItems}
        />
      </Card>

      {/* Threat Detail Modal */}
      <ThreatDetailModal
        visible={threatDetailVisible}
        onClose={closeThreatDetail}
        threatData={selectedThreat}
      />
    </div>
  );
};
